import { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '@emotion/react';
import { useSelector } from 'react-redux';

import { useLessonsJourneyTrigger } from 'features/Onboarding';
import { ROUTES } from 'navigation/constants';
import { shouldSeeAllLessons } from 'features/Auth/selectors';
import { isUserOnboarded } from 'features/User/selectors';

import { useLessons } from '../queries';
import { useMatchMedia } from 'theme/emotion';
import { LessonState, type LessonWithState } from '../types';
import { injectOnboardingLesson } from '../utils/injectOnboardingLesson';
import { handleOnboardingClick } from '../utils/handleOnboardingClick';

export const useLessonsService = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { mq } = useTheme();
  const isTablet = useMatchMedia([mq.md.up, mq.lg.down]);
  const canSeeAllLessons = useSelector(shouldSeeAllLessons);
  const isOnboarded = useSelector(isUserOnboarded);

  const { data, isLoading } = useLessons();

  useLessonsJourneyTrigger(!isLoading && !!data?.lessons.length);

  const lessons = useMemo<LessonWithState[]>(
    () => {
      // Filter lessons based on tier - free tier users only see unlocked lessons
      const filteredLessons = canSeeAllLessons
        ? data?.lessons
        : data?.lessons.filter(lesson => lesson.unlocked);

      if (!filteredLessons) return [];

      // Inject onboarding lesson at the beginning
      const lessonsWithOnboarding = injectOnboardingLesson(filteredLessons, isOnboarded);

      // Map lessons to include progress (0-1 scale)
      const lessonsWithProgress = lessonsWithOnboarding.map(lesson => ({
        ...lesson,
        progress: lesson.max_points > 0 ? lesson.points_earned / lesson.max_points : 0,
      }));

      // Find the next lesson (first lesson with progress < 1)
      const nextLesson = lessonsWithProgress.find(lesson => lesson.progress < 1) || lessonsWithProgress[0];
      const nextLessonIndex = nextLesson ? lessonsWithProgress.findIndex(l => l.id === nextLesson.id) : -1;

      return lessonsWithProgress.map(
        ({
          id,
          type,
          order,
          name,
          description,
          emoji,
          tasks_total,
          tasks_completed,
          points_earned,
          max_points,
          progress,
        }, index) => {
          const hasBeenStarted = !!tasks_completed && tasks_total !== tasks_completed;

          const onClick = () => {
            handleOnboardingClick(navigate, id, type);
          };

          // Determine lesson state
          let state: LessonState = LessonState.IDLE;
          if (nextLesson && id === nextLesson.id) {
            state = nextLessonIndex === 0 ? LessonState.START : LessonState.CONTINUE;
          }

          return {
            id,
            label: type === 'onboarding' ? 'Onboarding' : `Lesson ${order}`,
            title: name,
            description,
            emoji,
            hasBeenStarted,
            progress: progress * 100, // Convert to percentage for UI
            onClick,
            state,
          };
        }
      );
    },
    [data, navigate, canSeeAllLessons, isOnboarded]
  );

  const placeholders = useMemo(() => [], []);

  const tabs = useMemo(
    () =>
      [
        { label: 'Roleplays', to: ROUTES.ROLEPLAYS.path },
        { label: 'Lessons', to: ROUTES.HOME.path },
      ].map(tab => ({ ...tab, active: tab.to === pathname })),
    [pathname]
  );

  const suggestionsCardIndex = useMemo(() => (isTablet ? 1 : 2), [isTablet]);

  const nextLessonId = useMemo(() => {
    const nextLesson = lessons.find(lesson => lesson.state !== LessonState.IDLE);
    return nextLesson?.id;
  }, [lessons]);

  return {
    isLoading,
    tabs,
    lessonsCompleted: data?.lessons_completed ?? 0,
    lessonsTotal: data?.lessons_total ?? 0,
    pointsEarned: data?.points_earned ?? 0,
    lessons,
    placeholders,
    suggestionsCardIndex,
    nextLessonId,
  };
};